import { DeleteOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Form, Input, Progress, Typography } from "antd";
import type { UploadFile } from "antd/es/upload";
import { useEffect, useState } from "react";
import { formatFileSize } from "../utils/formatFileSize";
import { isImageContentType } from "../utils/getFileTypeIcon";
import styles from "./FileListItem.module.css";
import { FileThumbnail } from "./FileThumbnail";

type FileResponse = {
  file_path?: string | null;
  content_type?: string | null;
};

type FileListItemProps = {
  file: UploadFile<FileResponse>;
  onRemove: () => void;
  intl: IntlShape;
  fieldPrefix?: number;
  showDescription?: boolean;
};

function useLocalPreviewUrl(file: UploadFile<FileResponse>): string | undefined {
  const [url, setUrl] = useState<string | undefined>();

  useEffect(() => {
    const raw = file.originFileObj;
    if (!raw || !isImageContentType(raw.type)) {
      return;
    }
    const objectUrl = URL.createObjectURL(raw);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setUrl(undefined);
    };
  }, [file.originFileObj]);

  return url;
}

export const FileListItem = ({
  file,
  onRemove,
  intl,
  fieldPrefix,
  showDescription = false,
}: FileListItemProps) => {
  const isError = file.status === "error";
  const isUploading = file.status === "uploading";
  const filepath = file.response?.file_path ?? "";
  const isDone = file.status === "done" && !!filepath;
  const size = formatFileSize(file.size);
  const thumbnailSrc = useLocalPreviewUrl(file);

  const namePath = (key: string) =>
    fieldPrefix !== undefined
      ? [fieldPrefix, "new_files", filepath, key]
      : ["new_files", filepath, key];

  return (
    <div className={`${styles.fileItem} ${isError ? styles.fileItemError : ""}`}>
      <FileThumbnail
        src={thumbnailSrc}
        contentType={file.response?.content_type ?? file.type}
        name={file.name}
        size={28}
      />
      <div className={styles.fileName}>
        <Typography.Text ellipsis={{ tooltip: file.name }}>{file.name}</Typography.Text>
        {size && (
          <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
            {size}
          </Typography.Text>
        )}
      </div>
      {isUploading && (
        <Progress
          percent={Math.round(file.percent ?? 0)}
          size="small"
          style={{ width: 160, flexShrink: 0 }}
        />
      )}
      {isDone && showDescription && (
        <div className={styles.descriptionInput}>
          <Form.Item
            name={namePath("description")}
            label={false}
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder={intl.formatMessage({ id: "table_column_description" })}
              maxLength={255}
            />
          </Form.Item>
        </div>
      )}
      {isError && (
        <Typography.Text type="danger" style={{ flexShrink: 0 }}>
          {intl.formatMessage({ id: "failed_to_upload" })}
        </Typography.Text>
      )}
      <Button
        type="text"
        size="small"
        danger
        icon={<DeleteOutlined />}
        onClick={onRemove}
      />
    </div>
  );
};
